import { authRedirectCheckRtkQuery } from '@autone/auth';
import { isAutoneBackendError, isFetchBaseQueryError } from '@autone/utils';
import {
  fetchBaseQuery,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { enqueueSnackbar } from 'notistack';

import { errorSnackBar } from '../error';

import {
  type CustomBaseQueryType,
  type QueryReturnValue,
} from './customBaseQuery.types';

// base url host
const protocol = import.meta.env.MODE === 'test' ? 'http://' : 'https://';
const backendHost = `${protocol}${import.meta.env.VITE_BACKEND_ENDPOINT_HOST}`;

// this is our base query for rtk query
// we pass in url and check if the user is authenticated before making an api request
// we also add the snackbar to all requests
export const customBaseQuery: CustomBaseQueryType<unknown> = async (
  args,
  api,
  extraOptions,
  url,
  /**
   * Local development only. When provided, requests are routed to
   * `http://localhost:<localhostPort>` instead of the configured base URL.
   * note each individual BE app runs on a different port number
   * Shouldn't be used in production
   */
  localhostPort,
) => {
  const host = localhostPort
    ? `http://localhost:${localhostPort}`
    : backendHost;
  const pathname = localhostPort ? url?.replace('v2/', '') : url;

  const baseUrl = `${host}/${pathname}`;
  const prepareHeaders = async (headers: Headers) => {
    const { idToken, accessToken, authProvider } =
      (await authRedirectCheckRtkQuery()) || {};
    headers.set('Authorization', `Bearer ${idToken}`);
    headers.set('x-api-key', `${idToken}`);
    headers.set('x-api-access-key', `${accessToken}`);
    headers.set('x-auth-provider', `${authProvider}`);
    return headers;
  };

  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
  });
  // if args is a string we don't need to destructure extra args as it is not passed in
  if (typeof args === 'string') {
    const result = await baseQuery(args, api, extraOptions);

    // if there is an error and we don't want to hide the snackbar, show the snackbar
    if (result.error) {
      errorSnackBar(enqueueSnackbar, result.error);
    }
    return result;
  }

  const { extraArgs } = args;

  const { hideSnackbar, getErrors } = extraArgs || {};

  const result = await baseQuery(args, api, extraOptions);

  // if there is an error and we don't want to hide the snackbar, show the snackbar
  if (!hideSnackbar) {
    dispatchSnackbar(result, getErrors);
  }

  return result;
};

export const dispatchSnackbar = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  getErrors?: () => Record<string, string>,
) => {
  if (
    getErrors &&
    isFetchBaseQueryError(result.error) &&
    isAutoneBackendError(result.error.data)
  ) {
    errorSnackBar(
      enqueueSnackbar,
      result.error,
      getErrors()[result.error.data.error_code],
    );
  } else if (result.error) {
    errorSnackBar(enqueueSnackbar, result.error);
  }
};
